import * as React from "react";
import { useQueries } from "@tanstack/react-query";
import { Box, Typography } from "@mui/material";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { EmptyState } from "@/components/dashboard/EmptyState";
import { WatchlistTable, type WatchlistRow } from "./WatchlistTable";
import { quoteService } from "@/services/quoteService";
import type { InstrumentSummary } from "./InstrumentSearch";

interface QuotePayload {
  bid_price: number;
  ask_price: number;
  last_traded_price: number;
}

interface WatchlistSectionProps {
  watchlist: InstrumentSummary[];
}

/**
 * Watchlist membership is local presentation state only (added via
 * Instrument Search) - the backend has no persisted watchlist concept.
 * Live prices are fetched per-instrument via `useQueries` (one real
 * `GET /market-data/quote` call per watched instrument), then assembled
 * into a single rows array for the reused `DataTable`.
 */
export function WatchlistSection({ watchlist }: WatchlistSectionProps): React.JSX.Element {
  const quoteQueries = useQueries({
    queries: watchlist.map((instrument) => ({
      queryKey: ["market-data", "quote", instrument.instrument_token],
      queryFn: () => quoteService.getQuote(instrument.instrument_token),
    })),
  });

  const rows: WatchlistRow[] = watchlist.map((instrument, index) => {
    const query = quoteQueries[index];
    const quote = query.data as unknown as QuotePayload | undefined;
    return {
      instrument_token: instrument.instrument_token,
      trading_symbol: instrument.trading_symbol,
      exchange_code: instrument.exchange_code,
      last_price: quote?.last_traded_price ?? null,
      provider: null,
      updateStatus: query.isError ? "error" : query.isLoading ? "loading" : "success",
    };
  });

  return (
    <StatusCard title="Watchlist">
      {watchlist.length === 0 ? (
        <EmptyState message="Search for an instrument and add it to build your watchlist." />
      ) : (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            Read-only — prices refresh only when this page reloads or a query is retried.
          </Typography>
          <WatchlistTable rows={rows} />
        </Box>
      )}
    </StatusCard>
  );
}
